import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { adminAuthAPI } from '../api';
import { useAdminAuth } from '../App';

// Shown after a first login on a freshly-seeded admin account. At this point
// the client holds only a restricted session cookie (scope: password_change) that the
// backend accepts ONLY on /auth/change-password — every other route 403s until
// the seeded password is replaced.
export default function ForcePasswordChange() {
  const { login }  = useAdminAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const admin      = location.state?.admin;

  const [form, setForm]     = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setError(''); };

  // Reached without going through login (e.g. a refresh dropped router state) —
  // restart the flow rather than show a half-authenticated screen.
  if (!admin) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) { setError('New passwords do not match'); return; }
    if (form.newPassword.length < 12) { setError('New password must be at least 12 characters'); return; }
    if (!/[a-z]/.test(form.newPassword) || !/[A-Z]/.test(form.newPassword) || !/[0-9]/.test(form.newPassword)) {
      setError('New password must include lowercase, uppercase, and a number'); return;
    }
    if (form.newPassword === form.currentPassword) { setError('New password must be different from the current one'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await adminAuthAPI.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      // Backend retires the seeded credential and sets a full-access session cookie.
      login(data.admin || admin);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '2rem', width: '100%', maxWidth: '420px' }}>
        <h1 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Set a new password</h1>
        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1.5rem' }}>
          For security, the initial password must be changed before you can continue.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Current password</label>
            <input type="password" value={form.currentPassword} onChange={set('currentPassword')} required autoFocus autoComplete="current-password" />
          </div>
          <div className="form-group">
            <label>New password</label>
            <input type="password" value={form.newPassword} onChange={set('newPassword')} required autoComplete="new-password" minLength={12} />
          </div>
          <div className="form-group">
            <label>Confirm new password</label>
            <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required autoComplete="new-password" minLength={12} />
          </div>
          {error && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}
          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={saving}>
            {saving ? 'Saving…' : 'Set password & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
